// Xero billing (Session ARY, Item 4). Recurring contract billing runs on Xero's own repeating
// invoice templates, never the platform's own recurring invoicing (Session AKF retired that). The
// platform links a contract to the template(s) that bill it (a person does the linking; the
// platform may suggest one), keeps a cache of what Xero said, and derives a verdict on every read:
// is Xero billing what the contract says. Nothing here ever writes to Xero, and there is NO
// "update the template" action (Xero's API cannot update a repeating invoice): the office corrects
// the amount in Xero itself, and the flag clears at the next check.
//
// Mounted once per contract, on the Details tab of ContractDetailPage.tsx.
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import type {
  XeroBillingLink,
  XeroBillingMode,
  XeroBillingResult,
  XeroBillingView,
  XeroTemplateCandidate,
  XeroTemplateCandidates,
} from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { Button } from "../../components/Button";
import { ConfirmButton } from "../../components/ConfirmButton";
import { ModalShell, MODAL_ACTIONS, MODAL_ACTION_BTN } from "../../components/ModalShell";
import { StatusPill } from "../../components/StatusPill";
import { formatDateNumeric, formatTime } from "../../lib/format";
import { GuvnorBillingSection } from "./GuvnorBillingSection";

const INPUT = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green";
const RETRY_BTN = "rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-strong disabled:opacity-50";

function penceGbp(pence: number): string {
  return `£${(pence / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// A stored INSTANT (lastCheckedAt) as the London wall clock reads it, DD/MM/YYYY at HH:mm -- never
// the browser's own zone (the platform's time-zone rendering rule).
function numericDateTime(iso: string | null): string {
  if (!iso) return "";
  const date = formatDateNumeric(iso);
  return date === "-" ? "" : `${date} at ${formatTime(iso)}`;
}

// A calendar DAY the server already worked out (nextScheduledDate, YYYY-MM-DD). It is a day, not an
// instant, so it is read and printed in UTC: converting it through any other zone could move it a day.
function numericDay(ymd: string): string {
  const out = formatDateNumeric(`${ymd}T00:00:00.000Z`);
  return out === "-" ? ymd : out;
}

function freshnessLine(result: XeroBillingResult): string {
  if (!result.lastCheckedAt) return "Not read from Xero yet.";
  const base = `Last read from Xero: ${numericDateTime(result.lastCheckedAt)}`;
  return result.stale ? `${base} (over two days ago).` : `${base}.`;
}

// A repeating line's own amount: lineAmountPence when Xero sent one, else unit x quantity.
function lineAmountPence(line: { unitAmountPence: number; quantity: number; lineAmountPence: number | null }): number {
  return line.lineAmountPence ?? Math.round(line.unitAmountPence * line.quantity);
}

function VerdictBlock({ result }: { result: XeroBillingResult }) {
  if (result.severity === "warning") {
    return (
      <div role="alert" className="mt-3 rounded-ele border border-amber-500 bg-amber-500/10 p-3">
        <p className="font-semibold text-amber-300">{result.headline}</p>
        {/* Body ink for the message: amber-300 is theme-mapped for the light office canvas, amber-200 is not. */}
        <p className="mt-1 text-sm text-ele-body">{result.message}</p>
      </div>
    );
  }
  if (result.severity === "info") {
    return (
      <div className="mt-3 rounded-ele border border-ele-purple-accent bg-ele-bg p-3">
        <p className="font-semibold text-ele-strong">{result.headline}</p>
        <p className="mt-1 text-sm text-ele-body">{result.message}</p>
      </div>
    );
  }
  // ok: a quiet green-text headline, never a coloured left stripe or a highlighted box (the house
  // no-left-stripe rule, and there is nothing here that needs the office's attention).
  return (
    <div className="mt-3">
      <p className="font-semibold text-ele-green">{result.headline}</p>
      <p className="mt-1 text-sm text-ele-body">{result.message}</p>
    </div>
  );
}

function LinkedTemplatesTable({
  links,
  onUnlink,
  unlinkBusyId,
}: {
  links: XeroBillingLink[];
  onUnlink: (linkId: string) => void;
  unlinkBusyId: string | null;
}) {
  return (
    <div className="mt-3 overflow-hidden rounded-ele border border-ele-purple-accent">
      <table className="ele-list-table w-full text-left text-xs">
        <colgroup>
          <col style={{ width: "20%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "8%" }} />
        </colgroup>
        <thead className="bg-ele-inset font-menu text-[11px] uppercase tracking-widest text-ele-blue">
          <tr>
            <th className="px-3 py-2">Reference</th>
            <th className="px-3 py-2">Xero contact</th>
            <th className="px-3 py-2">Schedule</th>
            <th className="px-3 py-2 text-right">Amount ex VAT</th>
            <th className="px-3 py-2">Next invoice</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2" aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {links.map((l) => {
            const showExtra = Boolean(l.lastCheckError) || l.lines.length > 1 || Boolean(l.carriedFromContractId);
            return (
              <Fragment key={l.id}>
                <tr className="border-t border-ele-purple-accent">
                  <td className="truncate px-3 py-2 align-top">{l.reference || l.xeroRepeatingInvoiceId}</td>
                  <td className="truncate px-3 py-2 align-top">{l.contactName ?? "Unknown contact"}</td>
                  <td className="truncate px-3 py-2 align-top">{l.frequencyLabel}</td>
                  <td className="truncate px-3 py-2 text-right align-top">{l.subTotalPence == null ? "Not recorded" : penceGbp(l.subTotalPence)}</td>
                  <td className="truncate px-3 py-2 align-top">{l.nextScheduledDate ? numericDay(l.nextScheduledDate) : "-"}</td>
                  <td className="px-3 py-2 align-top">
                    <StatusPill domain="xeroTemplate" status={l.status} />
                  </td>
                  <td className="cell-actions px-3 py-2 text-right align-top">
                    <ConfirmButton
                      onConfirm={() => onUnlink(l.id)}
                      busy={unlinkBusyId === l.id}
                      busyLabel="Unlinking..."
                      label="Unlink"
                      confirmLabel="Confirm?"
                      className="font-menu text-xs tracking-wide text-ele-danger hover:underline"
                    />
                  </td>
                </tr>
                {showExtra && (
                  <tr className="border-t border-ele-purple-accent bg-ele-surface-soft">
                    <td colSpan={7} className="cell-wrap px-3 py-2 text-[11px] text-ele-body">
                      {l.carriedFromContractId && <p>Carried over from the previous agreement.</p>}
                      {l.lines.length > 1 && (
                        <ul className="mt-1 space-y-0.5">
                          {l.lines.map((line, i) => (
                            <li key={i}>
                              {line.description} · {penceGbp(lineAmountPence(line))}
                            </li>
                          ))}
                        </ul>
                      )}
                      {l.lastCheckError && <p className="mt-1 text-ele-danger">{l.lastCheckError}</p>}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// The mode block, shown only when there are no links (the contract has no Xero template, so the
// office either records how it IS billed, or leaves the question open).
function BillingModeBlock({
  mode,
  modeReason,
  onSetNotRepeating,
  onUndo,
}: {
  mode: XeroBillingMode;
  modeReason: string | null;
  onSetNotRepeating: (reason: string) => Promise<void>;
  onUndo: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (mode === "not_repeating") {
    return (
      <div className="mt-3">
        <p className="text-sm text-ele-body">Not billed by a Xero repeating invoice: {modeReason}</p>
        <button
          type="button"
          onClick={() => {
            setBusy(true);
            setError(null);
            onUndo()
              .catch((err) => setError(friendlyApiError(err, "Could not undo this. Please try again.")))
              .finally(() => setBusy(false));
          }}
          disabled={busy}
          className={`mt-1 ${RETRY_BTN}`}
        >
          {busy ? "Undoing..." : "Undo"}
        </button>
        {error && (
          <p role="alert" className="mt-1 text-xs text-ele-danger">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="mt-3">
      {!editing ? (
        <button type="button" onClick={() => setEditing(true)} className="font-menu text-xs tracking-wide text-ele-body underline-offset-2 hover:text-ele-strong hover:underline">
          This contract is not billed by a Xero repeating invoice
        </button>
      ) : (
        <div className="rounded-ele border border-ele-purple-accent bg-ele-bg p-3">
          <label className="block text-sm">
            <span className="mb-1 block text-ele-body">How is it billed?</span>
            <textarea
              rows={2}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="For example: Billed by hand each quarter"
              className={INPUT}
            />
          </label>
          {error && (
            <p role="alert" className="mt-2 text-sm text-ele-danger">
              {error}
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={busy}
              onClick={() => {
                const trimmed = reason.trim();
                if (trimmed.length < 3) return setError("Say how it is billed, in at least 3 characters.");
                setBusy(true);
                setError(null);
                onSetNotRepeating(trimmed)
                  .then(() => {
                    setEditing(false);
                    setReason("");
                  })
                  .catch((err) => setError(friendlyApiError(err, "Could not save this. Please try again.")))
                  .finally(() => setBusy(false));
              }}
            >
              {busy ? "Saving..." : "Save"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setReason("");
                setError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// One candidate row in the picker. `disabledReason` is null exactly when the row can be selected.
function candidateDisabledReason(t: XeroTemplateCandidate): string | null {
  if (t.status === "DELETED") return "Deleted in Xero";
  if (t.linkedToThisContract) return "Already linked here";
  // Session ASX: the server decides (the shared template rule), and says why when it cannot be linked.
  if (t.linkRefusal !== undefined) return t.linkRefusal;
  // A server not yet redeployed sends no linkRefusal: its rule was one contract per template.
  if (t.linkedToActiveContracts.length > 0) {
    const first = t.linkedToActiveContracts[0];
    return `Already linked to ${first.siteName ?? first.customerName ?? "another contract"}`;
  }
  return null;
}

// Consolidated invoicing (Session ASX): a template that already bills this customer's other contracts and CAN be
// linked here would bill this site on the same Xero invoice. Said before the office links it.
function candidateShareNote(t: XeroTemplateCandidate): string | null {
  if (candidateDisabledReason(t) != null || t.linkedToActiveContracts.length === 0) return null;
  const places = t.linkedToActiveContracts.map((c) => c.siteName ?? c.customerName ?? "another contract").join(", ");
  return `Already bills this customer's contract at ${places}. Linking it here shares it: the check compares its total with all of them added together.`;
}

function TemplateOption({
  t,
  selected,
  onSelect,
  suggestedWhy,
}: {
  t: XeroTemplateCandidate;
  selected: boolean;
  onSelect: () => void;
  suggestedWhy: string | null;
}) {
  const disabledReason = candidateDisabledReason(t);
  const shareNote = candidateShareNote(t);
  const disabled = disabledReason != null;
  return (
    <label
      className={`mb-2 flex items-start gap-3 rounded-ele border p-3 ${
        disabled ? "cursor-not-allowed border-ele-purple-accent opacity-60" : "cursor-pointer border-ele-purple-accent"
      } ${selected ? "border-ele-green" : ""}`}
    >
      <input type="radio" name="xero-template" checked={selected} disabled={disabled} onChange={onSelect} className="mt-1 accent-ele-green" />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-semibold text-ele-strong">{t.reference || t.xeroRepeatingInvoiceId}</span>
          {t.onCustomerContact && <StatusPill word="This customer" tone="purple" />}
          {suggestedWhy && <span className="text-xs text-ele-green">Suggested: {suggestedWhy}</span>}
        </span>
        <span className="mt-0.5 block text-xs text-ele-body">
          {t.contactName ?? "Unknown contact"} · {t.frequencyLabel} · {t.subTotalPence == null ? "Not recorded" : penceGbp(t.subTotalPence)} · Next{" "}
          {t.nextScheduledDate ? numericDay(t.nextScheduledDate) : "not set"}
        </span>
        {disabledReason && <span className="mt-0.5 block text-xs text-ele-danger">{disabledReason}</span>}
        {shareNote && <span className="mt-0.5 block text-xs text-ele-body">{shareNote}</span>}
      </span>
    </label>
  );
}

function TemplatePickerModal({
  contractId,
  onClose,
  onLinked,
}: {
  contractId: string;
  onClose: () => void;
  onLinked: (view: XeroBillingView) => void;
}) {
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [candidates, setCandidates] = useState<XeroTemplateCandidates | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setState("loading");
    setLoadError(null);
    api.contracts
      .xeroBillingTemplates(contractId)
      .then((c) => {
        if (!live) return;
        setCandidates(c);
        setState("ready");
        if (c.suggestion) setSelectedId(c.suggestion.xeroRepeatingInvoiceId);
      })
      .catch((err) => {
        if (!live) return;
        setLoadError(friendlyApiError(err, "Could not read Xero. Please try again."));
        setState("error");
      });
    return () => {
      live = false;
    };
  }, [contractId]);

  async function link() {
    if (!selectedId || busy) return;
    setBusy(true);
    setLinkError(null);
    try {
      const res = await api.contracts.xeroBillingLink(contractId, selectedId);
      onLinked(res.view);
    } catch (err) {
      setLinkError(friendlyApiError(err, "Could not link this repeating invoice. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Link a Xero repeating invoice" onClose={() => !busy && onClose()} width="max-w-[560px]">
      <h2 className="mb-2 font-heading text-xl text-ele-strong">Link a Xero repeating invoice</h2>
      {state === "loading" && <p className="text-sm text-ele-body">Reading Xero...</p>}
      {state === "error" && (
        <p role="alert" className="text-sm text-ele-danger">
          {loadError}
        </p>
      )}
      {state === "ready" && candidates && (
        <>
          {!candidates.suggestion && candidates.noSuggestionBecause && (
            <p className="mb-2 text-xs text-ele-body opacity-70">{candidates.noSuggestionBecause}</p>
          )}
          {candidates.templates.length === 0 ? (
            <p className="text-sm text-ele-body">No sales repeating invoices were found in Xero.</p>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {candidates.templates.map((t) => (
                <TemplateOption
                  key={t.xeroRepeatingInvoiceId}
                  t={t}
                  selected={selectedId === t.xeroRepeatingInvoiceId}
                  onSelect={() => setSelectedId(t.xeroRepeatingInvoiceId)}
                  suggestedWhy={candidates.suggestion?.xeroRepeatingInvoiceId === t.xeroRepeatingInvoiceId ? candidates.suggestion.why : null}
                />
              ))}
            </div>
          )}
          {linkError && (
            <p role="alert" className="mt-2 text-sm text-ele-danger">
              {linkError}
            </p>
          )}
        </>
      )}
      <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
        <button
          type="button"
          onClick={() => !busy && onClose()}
          disabled={busy}
          className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body ${MODAL_ACTION_BTN}`}
        >
          Cancel
        </button>
        <Button className={MODAL_ACTION_BTN} onClick={() => void link()} disabled={!selectedId || busy || state !== "ready"}>
          {busy ? "Linking..." : "Link this repeating invoice"}
        </Button>
      </div>
    </ModalShell>
  );
}

export function XeroBillingPanel({ contractId }: { contractId: string }) {
  const [view, setView] = useState<XeroBillingView | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [checking, setChecking] = useState(false);
  const [checkNotice, setCheckNotice] = useState<string | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [unlinkBusyId, setUnlinkBusyId] = useState<string | null>(null);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Every view this panel shows bumps the sequence, so a slow load that answers after a Check now, a link or an
  // unlink cannot put the older verdict back over the newer one (review finding).
  const seq = useRef(0);
  const show = useCallback((v: XeroBillingView) => {
    seq.current += 1;
    setView(v);
    setLoadState("ready");
  }, []);

  const load = useCallback(() => {
    const mine = ++seq.current;
    setLoadState("loading");
    api.contracts
      .xeroBilling(contractId)
      .then((v) => {
        if (seq.current !== mine) return;
        setView(v);
        setLoadState("ready");
      })
      .catch(() => {
        if (seq.current === mine) setLoadState("error");
      });
  }, [contractId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function checkNow() {
    if (checking) return;
    setChecking(true);
    setCheckError(null);
    setCheckNotice(null);
    try {
      const res = await api.contracts.xeroBillingCheck(contractId);
      show(res.view);
      const { checked, failed } = res.refresh;
      setCheckNotice(`Checked now: ${checked} template${checked === 1 ? "" : "s"} read${failed > 0 ? `, ${failed} failed` : ""}.`);
    } catch (err) {
      setCheckError(friendlyApiError(err, "Could not check Xero. Please try again."));
    } finally {
      setChecking(false);
    }
  }

  async function unlink(linkId: string) {
    setUnlinkBusyId(linkId);
    setUnlinkError(null);
    try {
      const res = await api.contracts.xeroBillingUnlink(contractId, linkId);
      show(res.view);
    } catch (err) {
      setUnlinkError(friendlyApiError(err, "Could not unlink this template. Please try again."));
    } finally {
      setUnlinkBusyId(null);
    }
  }

  return (
    <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <h2 className="mb-2 font-heading text-lg text-ele-strong">Xero billing</h2>
      <p className="text-sm text-ele-body">
        Recurring billing for this contract runs through Xero&rsquo;s own repeating invoices, unless it is switched to Guvnor billing below. The
        platform checks a linked repeating invoice against this contract every day and straight after any price change, and never changes one.
      </p>

      {loadState === "loading" && <p className="mt-3 text-sm text-ele-body">Loading Xero billing...</p>}
      {loadState === "error" && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <p role="alert" className="text-sm text-ele-danger">
            Could not load the Xero billing check.
          </p>
          <button type="button" onClick={() => load()} className={RETRY_BTN}>
            Retry
          </button>
        </div>
      )}

      {loadState === "ready" && view && (
        <>
          <VerdictBlock result={view.result} />
          {view.result.sharedWith && view.result.sharedWith.length > 0 && (
            // Consolidated invoicing (Session ASX): the one Xero invoice bills these contracts too, and the check
            // compares its total with all of them added together.
            <p className="mt-2 text-xs text-ele-body">
              Shared Xero invoice: also bills{" "}
              {view.result.sharedWith
                .map((s) => `${s.siteName ?? "another contract"} (${s.expectedPerPeriodPence == null ? "no price" : penceGbp(s.expectedPerPeriodPence)})`)
                .join(", ")}
              {view.result.ownExpectedPerPeriodPence != null && `, and this contract's own part is ${penceGbp(view.result.ownExpectedPerPeriodPence)}`} per{" "}
              {view.result.periodLabel}.
            </p>
          )}
          <p className="mt-2 text-xs text-ele-body opacity-70">{freshnessLine(view.result)}</p>

          {view.links.length > 0 && <LinkedTemplatesTable links={view.links} onUnlink={unlink} unlinkBusyId={unlinkBusyId} />}
          {unlinkError && (
            <p role="alert" className="mt-2 text-xs text-ele-danger">
              {unlinkError}
            </p>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => void checkNow()} disabled={checking} className={RETRY_BTN}>
              {checking ? "Checking..." : "Check now"}
            </button>
            {view.mode !== "not_repeating" && view.mode !== "guvnor" && (
              <Button type="button" variant="secondary" size="sm" onClick={() => setPickerOpen(true)}>
                Link a Xero repeating invoice
              </Button>
            )}
          </div>
          {checkNotice && (
            <p role="status" className="mt-2 text-xs text-ele-green">
              {checkNotice}
            </p>
          )}
          {checkError && (
            <p role="alert" className="mt-2 text-xs text-ele-danger">
              {checkError}
            </p>
          )}

          {view.links.length === 0 && view.mode !== "guvnor" && (
            <BillingModeBlock
              mode={view.mode}
              modeReason={view.modeReason}
              onSetNotRepeating={async (reason) => {
                const res = await api.contracts.xeroBillingSetMode(contractId, { mode: "not_repeating", reason });
                show(res.view);
              }}
              onUndo={async () => {
                const res = await api.contracts.xeroBillingSetMode(contractId, { mode: "unset" });
                show(res.view);
              }}
            />
          )}
          {/* Session BBZ: Guvnor billing, the platform raising this contract's Xero drafts itself. */}
          <GuvnorBillingSection contractId={contractId} onChanged={() => void load()} />
        </>
      )}

      {pickerOpen && (
        <TemplatePickerModal
          contractId={contractId}
          onClose={() => setPickerOpen(false)}
          onLinked={(v) => {
            show(v);
            setPickerOpen(false);
          }}
        />
      )}
    </section>
  );
}
